import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useLiveData } from "@/lib/useLiveData";
import { Droplets, Trash2, Wrench, Truck, ArrowRight } from "lucide-react";
import { WATER_TRUCK_URL, TRUCK_BIN_URL, TRUCK_TRIP_URL, formatXCD } from "@/lib/constants";
import { usePortal, PORTAL_TRUCKING } from "@/context/PortalContext";

export default function TruckingPortalPage() {
  const { setPortal } = usePortal();
  const [content, setContent] = useState({});
  const [pricing, setPricing] = useState(null);
  useLiveData(() => {
    setPortal(PORTAL_TRUCKING);
    api.get("/content").then(r => setContent(r.data)).catch(() => {});
    api.get("/pricing").then(r => setPricing(r.data)).catch(() => {});
  });

  const waterMin = pricing ? Math.min(
    ...((pricing.water_tiers || []).filter(t => t.active !== false).map(t => Number(t.price)).filter(n => n > 0))
  ) : null;
  const diagMin = pricing ? Math.min(...Object.values(pricing.diagnostics || {}).map(Number).filter(n => n > 0)) : null;

  const TILES = [
    { to: "/logistics/water", icon: Droplets, title: "Water Delivery", desc: "Drums, 275 – 3,000 gallons delivered direct to your tank.", price: (waterMin && Number.isFinite(waterMin)) ? `from ${formatXCD(waterMin)}` : "Book now", img: WATER_TRUCK_URL, tid: "tp-water" },
    { to: "/logistics/bin", icon: Trash2, title: "Bin Rentals", desc: "Heavy-duty bins for construction & cleanup jobs.", price: pricing?.bin ? formatXCD(pricing.bin) : "Book now", img: TRUCK_BIN_URL, tid: "tp-bin" },
    { to: "/logistics/trucking", icon: Truck, title: "Trucking Services", desc: "Aggregate, sand, gravel, pallets, equipment & general hauling.", price: "Quote", img: TRUCK_TRIP_URL, tid: "tp-trucking" },
    { to: "/logistics/diagnostics", icon: Wrench, title: "Vehicle Diagnostics", desc: "Honest, accurate diagnostics for cars and trucks.", price: diagMin ? `from ${formatXCD(diagMin)}` : "Book now", img: null, tid: "tp-diag" },
  ];

  return (
    <Layout>
      <section className="relative bg-[#0F172A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={WATER_TRUCK_URL} alt="Cleavie's water truck" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Portal · Trucking & Industrial Services</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl">
            {content.trucking_tagline || "Reliable trucking & industrial services across Antigua."}
          </h1>
          <p className="mt-4 max-w-xl text-white/85">Water, bins, diagnostics and hauling — booked online and dispatched by our crew.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-2 gap-6">
        {TILES.map(t => (
          <Link key={t.to} to={t.to} data-testid={t.tid}
                className="group block bg-white border border-slate-200 rounded-md overflow-hidden hover:border-slate-400 hover:-translate-y-1 transition-all">
            <div className="grid grid-cols-5">
              <div className="col-span-2 relative bg-slate-100 min-h-[180px]">
                {t.img ? (
                  <img src={t.img} alt={t.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1D4ED8] to-[#0F172A] flex items-center justify-center">
                    <t.icon className="h-16 w-16 text-white/80" strokeWidth={1.5} />
                  </div>
                )}
              </div>
              <div className="col-span-3 p-6">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1D4ED8]">{t.price}</div>
                <div className="flex items-center gap-2 mt-2 text-slate-900">
                  <t.icon className="h-6 w-6 text-[#E32636]" strokeWidth={2.5} />
                  <span className="font-heading font-bold text-xl">{t.title}</span>
                </div>
                <p className="text-sm text-slate-600 mt-2">{t.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1 font-bold text-slate-900">
                  Book now <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </section>
    </Layout>
  );
}
